import { airlinesLabel } from "./airlines";
import { CABIN_OPTIONS } from "./constants";
import { nightsBetween } from "./estimate";
import { formatShortTripDates } from "./trip-dates";
import type { Workspace } from "./types";
import { displayCity, homeCities, travelerCount } from "./workspace";

/** What each segment of the trip bar shows; null means the segment shows its placeholder. */
export interface TripSummary {
  who: string | null;
  where: string | null;
  when: string | null;
  /** "3 nights", shown beside the When title so the date range never has to share its line. */
  nights: string | null;
  /** "Economy · USD · No Spirit", or "Mixed cabins · USD" when a group flies another cabin. */
  options: string;
  /** The same, short, for a narrow bar: "Econ · USD · No NK", "Mixed · USD". */
  optionsShort: string;
}

const CABIN_SHORT: Record<Workspace["cabin"], string> = {
  economy: "Econ",
  "premium economy": "Prem",
  business: "Biz",
  first: "First",
};

const people = (count: number): string => `${count.toLocaleString("en-US")} ${count === 1 ? "person" : "people"}`;

/**
 * The trip bar's one-line summaries: "11 people · San Francisco +1", "Las Vegas +2",
 * "Nov 17–20" with "3 nights", "Economy · USD · No Spirit" (or "Mixed cabins · USD" when groups
 * fly different cabins).
 * @param workspace - The workspace.
 * @returns One summary per segment.
 * @throws Never.
 */
export const summarizeTrip = (workspace: Workspace): TripSummary => {
  const cities = homeCities(workspace.origins);
  const count = travelerCount(workspace);
  const more = (total: number) => (total > 1 ? ` +${total - 1}` : "");
  const nights = nightsBetween(workspace.dates);
  // A group with its own cabin makes the trip-wide one only a default.
  const mixed = workspace.origins.some((origin) => origin.cabin && origin.cabin !== workspace.cabin);
  return {
    who: cities.length
      ? `${people(count)} · ${displayCity(cities[0])}${more(cities.length)}`
      : count > 0
        ? people(count)
        : null,
    where: workspace.destinations.length
      ? `${displayCity(workspace.destinations[0])}${more(workspace.destinations.length)}`
      : null,
    when: workspace.dates ? formatShortTripDates(workspace.dates) : null,
    nights: workspace.dates ? `${nights} ${nights === 1 ? "night" : "nights"}` : null,
    options: [
      mixed ? "Mixed cabins" : (CABIN_OPTIONS.find((option) => option.value === workspace.cabin)?.label ?? "Economy"),
      workspace.currency,
      airlinesLabel(workspace.airlines),
    ]
      .filter(Boolean)
      .join(" · "),
    optionsShort: [
      mixed ? "Mixed" : CABIN_SHORT[workspace.cabin],
      workspace.currency,
      airlinesLabel(workspace.airlines, true),
    ]
      .filter(Boolean)
      .join(" · "),
  };
};
